import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { SectionTitle } from 'components';
import { Link } from 'gatsby';
import { scrollToAnchor } from 'utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from '@reach/router';
import PrevArrowSVG from 'images/shared/PrevArrow';
import { isMobileOnly } from 'react-device-detect';
import { wrap } from 'utils/wrap';

const services = [
  {
    title: 'Brand & Campaign Strategy',
    summary:
      'Working with the world’s brightest healthcare brands, together we build campaigns that are not only medically and legally compliant, but also creatively compelling.',
    link: '/services/healthcare-brand-and-campaign-strategy',
    list: [
      'Unique Team Engagement',
      'Online Team Collaboration',
      'Team Building Gamification',
      'Team Created Marketing Strategy'
    ]
  },
  {
    title: 'Creative & Design',
    summary:
      'From brand identity and packaging to multimedia storytelling and digital interfaces, our creative team crafts compelling, compliant, and beautifully executed experiences across every medium.',
    link: '/services/creative-and-design',
    list: [
      'Original Creative Concepts',
      'Web Design & Development',
      'App Development',
      'Multimedia Creative Design'
    ]
  },
  {
    title: 'Web & App Development',
    summary:
      'From simple brochure websites, to a complex web application, or a complete digital transformation, our expert development team specialises in building fast, secure, and scalable solutions that perform exceptionally across all devices.',
    link: '/services/web-development',
    list: [
      'Online Audience Engagement',
      'Branded Online Studio',
      'Live/Prerecorded Shows & Events',
      'Post Event Communications'
    ]
  },
  {
    title: 'Conference<br aria-hidden="true" /> & Event Management',
    summary:
      'Expert design, planning and production management of all types of in-person, virtual & hybrid events and conferences by our specialist in house Agency X team.',
    link: '/services/healthcare-event-management/',
    list: [
      'Insights & Strategy Development',
      'Campaign Creative & Design',
      'Project Management',
      'Campaign Analytics'
    ]
  }
];

const serviceVariants = {
  hover: {
    cursor: 'pointer',
    scale: 1.02,
    transition: {
      type: 'tween',
      ease: 'easeInOut',
      duration: 0.2
    }
  }
};

const variants = {
  enter: (direction) => {
    return {
      x: direction > 0 ? 100 : -100,
      opacity: 0
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 100 : -100,
      opacity: 0
    };
  }
};

const ServicesOverview = ({ compactTop }) => {
  const location = useLocation();
  const { pathname } = location;
  const [[page, direction], setPage] = useState([0, 0]);
  const paginate = (dir) => {
    setPage([page + dir, dir]);
  };
  const index = wrap(0, services.length, page);

  useEffect(() => {
    if (location.state?.scrollToServices) {
      scrollToAnchor('services');
    }
  }, [location.pathname]);

  return (
    <OuterContainer id="services" $compactTop={compactTop}>
      <div>
        {pathname === '/' && (
          <SectionTitle style={{ margin: '-0.1rem 0 0.05rem' }}>Services</SectionTitle>
        )}
        <Mobile>
          <MobileServices>
            <Container
              key={page}
              id={services[index]}
              alt="slide"
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                duration: 0.5
              }}
              onPan={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);
                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}
            >
              <AnimatePresence initial={false}>
                <motion.div key={services[index].title}>
                  <Link
                    to={services[index].link}
                    state={{ previousPageWasHomepage: true }}
                    title={services[index].title.replaceAll('<br aria-hidden="true" />', ' ')}
                    style={{ textDecoration: 'none' }}
                    id={`see-more-${services[index].title
                      .toLowerCase()
                      .replaceAll(' ', '-')
                      .replaceAll('&', 'and')}`}
                  >
                    <Service
                      variants={serviceVariants}
                      id={`see-more-${services[index].title
                        .toLowerCase()
                        .replaceAll(' ', '-')
                        .replaceAll('&', 'and')}`}
                    >
                      <motion.h3
                        variants={serviceVariants}
                        dangerouslySetInnerHTML={{ __html: services[index].title }}
                      />
                      <p>{services[index].summary}</p>
                      <SeeMore
                        variants={serviceVariants}
                        id={`see-more-${services[index].title
                          .toLowerCase()
                          .replaceAll(' ', '-')
                          .replaceAll('&', 'and')}`}
                      >
                        See More
                      </SeeMore>
                    </Service>
                  </Link>
                </motion.div>
              </AnimatePresence>
            </Container>
            <ButtonWrapper>
              <ButtonBack type="button" aria-label="Previous Service" onClick={() => paginate(-1)}>
                <PrevArrow />
              </ButtonBack>
              <ButtonNext type="button" aria-label="Next Service" onClick={() => paginate(1)}>
                <NextArrow />
              </ButtonNext>
            </ButtonWrapper>
          </MobileServices>
        </Mobile>
        <Desktop>
          <Services>
            {services.map((service) => (
              <motion.li
                key={service.title}
                variants={serviceVariants}
                whileHover="hover"
                whileTap="tap"
                layout
              >
                <Link
                  to={service.link}
                  state={{ previousPageWasHomepage: true }}
                  title={service.title.replaceAll('<br aria-hidden="true" />', ' ')}
                  style={{ textDecoration: 'none', margin: '0.5rem' }}
                  id={`see-more-${service.title
                    .toLowerCase()
                    .replaceAll(' ', '-')
                    .replaceAll('&', 'and')}`}
                >
                  <Service
                    variants={serviceVariants}
                    id={`see-more-${service.title
                      .toLowerCase()
                      .replaceAll(' ', '-')
                      .replaceAll('&', 'and')}`}
                  >
                    <motion.h3
                      variants={serviceVariants}
                      dangerouslySetInnerHTML={{ __html: service.title }}
                    />
                    <p>{service.summary}</p>
                    <SeeMore
                      variants={serviceVariants}
                      id={`see-more-${service.title
                        .toLowerCase()
                        .replaceAll(' ', '-')
                        .replaceAll('&', 'and')}`}
                    >
                      See More
                    </SeeMore>
                  </Service>
                </Link>
              </motion.li>
            ))}
          </Services>
        </Desktop>
      </div>
    </OuterContainer>
  );
};

const swipeConfidenceThreshold = 100;
const swipePower = (offset, velocity) => {
  return Math.abs(offset) * velocity;
};

const OuterContainer = styled.section`
  background-color: var(--ax-beige);
  > div {
    padding-top: ${({ $compactTop }) => ($compactTop ? '0.5rem' : '2.05rem')};
    grid-column: 2 / 4;
    max-width: 1577px;
    margin: 0 auto;
    width: 100%;

    /* The carousel adds unwanted padding around the slides. Hiding the overflow solves this. */
    overflow: hidden;

    @media screen and (min-width: 48rem) {
      grid-column: 2 / 3;
      padding: ${({ $compactTop }) => ($compactTop ? '2rem 0 6rem' : '6rem 0')};
      overflow: visible;
    }

    @media screen and (min-width: 62.5rem) {
      padding: ${({ $compactTop }) => ($compactTop ? '1.5rem 0 4rem' : '4rem 0')};
    }
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  position: absolute;
  left: initial;
  bottom: initial;
  right: 0.625rem !important;
  width: 7.188rem;
  top: -1rem !important;
  z-index: 2;
  height: 3rem;
`;

const ButtonStyle = css`
  align-items: center;
  background-color: var(--ax-gold);
  border-radius: 0.125rem;
  display: flex;
  height: 3rem;
  justify-content: center;
  outline: none;
  transition: background-color 150ms ease-in-out;
  width: 3rem;

  &[disabled] {
    background-color: var(--ax-grey);
  }

  &:active {
    background-color: #000;
    transition: background-color 75ms ease-in-out;
  }
`;

const ButtonBack = styled.button`
  ${ButtonStyle}
`;

const ButtonNext = styled.button`
  ${ButtonStyle}
`;

const PrevArrow = styled(PrevArrowSVG)`
  height: 0.938rem;
  width: 1.688rem;
`;

const NextArrow = styled(PrevArrow)`
  transform: scaleX(-1);
`;

const SeeMore = styled(motion.div)`
  align-items: center;
  border-radius: 0.125em;
  border: 0.063em solid var(--ax-gold);
  color: var(--ax-gold);
  display: flex;
  font-size: 1rem;
  font-weight: 700;
  height: 3em;
  justify-content: center;
  text-transform: uppercase;
  width: 10.375em;

  @media screen and (min-width: 48rem) {
    border: none;
    bottom: 0;
    color: #000;
    display: block;
    font-weight: 700;
    height: unset;
    margin-bottom: 0.3em;
    pointer-events: none;
    position: absolute;
    width: unset;
  }
`;

const MobileServices = styled.div`
  margin: 0 auto;
  position: relative;
  max-width: 100%;
  list-style-type: none;
  padding: 0;

  a {
    text-decoration: none;
  }

  @media screen and (max-width: 39.75rem) {
    max-width: 28.125rem;
  }

  @media screen and (min-width: 48rem) {
    display: grid;
    grid-gap: 95px 86px;
    grid-template-columns: repeat(2, 250px);
    grid-template-rows: 300px 300px;
    justify-content: center;
    padding-top: 3.7rem;
  }

  @media screen and (min-width: 80rem) {
    grid-template-rows: 300px;
    grid-template-columns: repeat(4, minmax(auto, 250px));
    justify-content: space-between;
    grid-gap: 0;
    /* max-width: 73.75rem; */
    width: 100%;
  }
`;

const Services = styled.ul`
  margin: 0 auto;
  position: relative;
  max-width: 100%;
  list-style-type: none;
  padding: 0;

  a {
    text-decoration: none;
  }

  @media screen and (max-width: 39.75rem) {
    max-width: 28.125rem;
  }

  @media screen and (min-width: 48rem) {
    display: grid;
    grid-gap: 95px 86px;
    grid-template-columns: repeat(2, 250px);
    grid-template-rows: 300px 300px;
    justify-content: center;
    padding-top: 3.7rem;
  }

  @media screen and (min-width: 1280px) {
    grid-template-rows: 300px;
    grid-template-columns: repeat(4, minmax(auto, 260px));
    justify-content: space-between;
    grid-gap: 0;
    /* max-width: 73.75rem; */
    width: 100%;
  }

  @media screen and (min-width: 1440px) {
    grid-template-columns: repeat(4, minmax(auto, 288px));
  }
`;

const Service = styled(motion.div)`
  background-color: #fff;
  height: 100%;
  margin-top: 1.1rem;
  max-height: 26.375rem;
  padding: 5rem 1.3rem 1.438rem 1.438rem;
  width: calc(100% - 1.25rem);

  ul {
    li {
      font-size: 1rem;
      line-height: 2em;
    }
  }

  @media screen and (min-width: 351px) {
    max-height: 30rem;
  }

  svg {
    margin-bottom: 1.28rem;
    width: 2.5rem;
    height: 2.313rem;

    g {
      transition: stroke 200ms ease-in-out;
      stroke-width: 1.25px;
    }
  }
  h3 {
    color: var(--ax-gold);
    margin-bottom: 0.6em;
    font-size: 2rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    text-wrap: wrap;
    transition: 0.3s;
    svg {
      width: auto;
      height: 0.9rem;
      margin-top: 1rem;
      margin-bottom: 0;
    }
  }

  p {
    color: #000;
    font-size: 0.875rem;
    line-height: 1.429em;
    margin-bottom: 3.3em;
    max-width: 92.5%;
  }

  ul {
    list-style-type: none;
    font-size: 1rem;
    line-height: 1.375em;
    color: #000;
    margin-bottom: 1.75em;
  }
  ${SeeMore} {
    transition: 0.3s;
  }
  &:hover {
    /* svg g {
      stroke: var(--ax-gold);
    } */
    h3 {
      color: var(--ax-gold);
      font-weight: 900;
    }
    ${SeeMore} {
      color: var(--ax-gold);
    }
  }

  &:not(:hover) {
    svg g {
      stroke: #000;
    }

    ul {
      font-weight: 400;
    }
  }

  @media screen and (min-width: 48rem) {
    background-color: transparent;
    margin-top: 0;
    padding: 0;
    width: 100%;
    position: relative;
    border-bottom-style: solid;
    border-bottom-width: 2px;
    border-bottom-color: #000;
    transition: 0.3s;
    &:hover {
      border-bottom: 2px solid var(--ax-gold);
    }

    svg {
      margin-bottom: 1.2rem;
    }

    h3 {
      font-weight: 100;
      margin-bottom: 1rem;
      color: #000;
      transition: font-weight 0.25s ease-in-out;
    }

    p {
      max-width: 100%;
    }

    ul {
      margin-bottom: 0;
    }
  }
`;

const Container = styled(motion.div)`
  width: 100%;
  height: auto;
  position: relative;
`;

const Mobile = styled.div`
  display: block;
  @media (min-width: 48rem) {
    display: none;
  }
`;

const Desktop = styled.div`
  display: none;
  @media (min-width: 48rem) {
    display: block;
  }
`;

export default ServicesOverview;
